import React, { useMemo, useState, useRef, useEffect } from "react";
import { FlightDetails, ConnectionFlight, FlightSegment } from "./types";
import { postFlightRequest } from "apis/loadsServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import DefaultAirLogo from "../../assets/images/logo.svg";
import { formatDateTime } from "utils/dateUtils";
import { fetchUserCreditBalance } from "apis/creditService";

// Skeleton Loader Component
const SkeletonBox = ({
  width = "100%",
  height = "1rem",
  className = "",
}: {
  width?: string | number;
  height?: string | number;
  className?: string;
}) => (
  <div
    className={`bg-gray-200 rounded animate-pulse ${className}`}
    style={{ width, height }}
  />
);

const FlightCardSkeleton = () => (
  <div className="bg-white rounded-lg p-5 border border-gray-200">
    <div className="flex items-center justify-between gap-4 mb-4">
      <div className="flex items-center gap-3">
        <SkeletonBox width="48px" height="48px" className="rounded" />
        <div>
          <SkeletonBox width="60px" height="12px" className="mb-2" />
          <SkeletonBox width="100px" height="14px" />
        </div>
      </div>
      <div>
        <SkeletonBox width="70px" height="12px" className="mb-2" />
        <SkeletonBox width="80px" height="14px" />
      </div>
    </div>
    <div className="bg-lightblue-100 p-4 rounded-lg flex items-center gap-6">
      <div className="flex flex-col items-start gap-1 min-w-[90px]">
        <SkeletonBox width="50px" height="14px" />
        <SkeletonBox width="80px" height="12px" className="mt-1" />
        <SkeletonBox width="70px" height="12px" className="mt-1" />
      </div>
      <div className="flex flex-col items-center w-full">
        <div className="flex items-center w-full">
          <SkeletonBox
            width="12px"
            height="12px"
            className="rounded-full flex-shrink-0"
          />
          <SkeletonBox width="100%" height="2px" className="mx-2" />
          <SkeletonBox width="16px" height="16px" />
          <SkeletonBox width="100%" height="2px" className="mx-2" />
          <SkeletonBox
            width="12px"
            height="12px"
            className="rounded-full flex-shrink-0"
          />
        </div>
        <SkeletonBox width="60px" height="12px" className="mt-1" />
      </div>
      <div className="flex flex-col items-start gap-1 min-w-[90px]">
        <SkeletonBox width="50px" height="14px" />
        <SkeletonBox width="80px" height="12px" className="mt-1" />
        <SkeletonBox width="70px" height="12px" className="mt-1" />
      </div>
    </div>
  </div>
);

interface SearchResultsProps {
  flights: FlightDetails[];
  connectionFlights?: ConnectionFlight[];
  stopsFilter: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
  onStopsFilterChange: (
    filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null,
  ) => void;
  isLoadingFlights?: boolean;
  show: boolean;
  searchDate: string;
  selectedDates?: string[];
  onPostRequestSuccess?: () => void;
  sourceLabel?: string;
  destinationLabel?: string;
  totalResults?: number;
  hideConnectedFilter?: boolean;
  hideInternalFilters?: boolean;
  readOnly?: boolean;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  flights,
  connectionFlights = [],
  stopsFilter,
  onStopsFilterChange,
  isLoadingFlights = false,
  show,
  searchDate,
  selectedDates = [],
  onPostRequestSuccess,
  sourceLabel,
  destinationLabel,
  totalResults = 0,
  hideConnectedFilter = false,
  hideInternalFilters = false,
  readOnly = false,
}) => {
  const { showToast } = useToastStore.getState();
  const { showLoader, hideLoader } = useGlobalLoader();

  const [selectedFlights, setSelectedFlights] = useState<FlightDetails[]>([]);
  const [selectedConnections, setSelectedConnections] = useState<
    ConnectionFlight[]
  >([]);

  // State for individual segment selection
  interface SelectedSegment {
    segment: FlightSegment;
    connectionId: string; // Parent connection's compositeId
  }
  const [selectedSegments, setSelectedSegments] = useState<SelectedSegment[]>(
    [],
  );

  const hasLiveData = (flight: FlightDetails) =>
    flight.liveStatus ||
    flight.delayMinutes ||
    flight.estimatedDeparture ||
    flight.estimatedArrival ||
    flight.actualDeparture ||
    flight.gate ||
    flight.terminal ||
    flight.liveDurationMinutes;

  const [isStopsDropdownOpen, setIsStopsDropdownOpen] = useState(false);
  const stopsDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        stopsDropdownRef.current &&
        !stopsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsStopsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Only clear selected flights and segments when search params (not page) change
  const prevSearchParams = useRef({
    stopsFilter,
    searchDate,
    selectedDates: JSON.stringify(selectedDates),
  });

  useEffect(() => {
    const paramsChanged =
      prevSearchParams.current.stopsFilter !== stopsFilter ||
      prevSearchParams.current.searchDate !== searchDate ||
      prevSearchParams.current.selectedDates !== JSON.stringify(selectedDates);

    if (paramsChanged) {
      setSelectedFlights([]);
      setSelectedConnections([]);
      setSelectedSegments([]);
    }

    prevSearchParams.current = {
      stopsFilter,
      searchDate,
      selectedDates: JSON.stringify(selectedDates),
    };
  }, [stopsFilter, searchDate, selectedDates]);

  const toggleFlightSelection = (flight: FlightDetails) => {
    const flightCompositeId = flight.compositeId || String(flight.id);
    const isSelected = selectedFlights.some((f) => {
      const fCompositeId = f.compositeId || String(f.id);
      return fCompositeId === flightCompositeId;
    });
    if (isSelected) {
      setSelectedFlights(
        selectedFlights.filter((f) => {
          const fCompositeId = f.compositeId || String(f.id);
          return fCompositeId !== flightCompositeId;
        }),
      );
    } else {
      setSelectedFlights([...selectedFlights, flight]); // Add the new flight to the list
    }
  };

  // Helper: Check if a specific segment is selected
  const isSegmentSelected = (
    segment: FlightSegment,
    connectionId: string,
  ): boolean => {
    const segmentCompositeId = segment.compositeId || String(segment.id);
    return selectedSegments.some((sel) => {
      const selSegmentId = sel.segment.compositeId || String(sel.segment.id);
      return (
        selSegmentId === segmentCompositeId && sel.connectionId === connectionId
      );
    });
  };

  // Helper: Check if all segments of a connection are selected
  const areAllSegmentsSelected = (connection: ConnectionFlight): boolean => {
    const connectionId =
      connection.compositeId ||
      `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;
    return connection.segments.every((segment) =>
      isSegmentSelected(segment, connectionId),
    );
  };

  // Toggle individual segment selection
  const toggleSegmentSelection = (
    segment: FlightSegment,
    connectionId: string,
    event?: React.MouseEvent,
  ) => {
    if (event) {
      event.stopPropagation(); // Prevent parent click handlers
    }

    const segmentCompositeId = segment.compositeId || String(segment.id);
    const isSelected = isSegmentSelected(segment, connectionId);

    if (isSelected) {
      // Remove segment
      setSelectedSegments(
        selectedSegments.filter((sel) => {
          const selSegmentId =
            sel.segment.compositeId || String(sel.segment.id);
          return !(
            selSegmentId === segmentCompositeId &&
            sel.connectionId === connectionId
          );
        }),
      );
    } else {
      // Add segment
      setSelectedSegments([...selectedSegments, { segment, connectionId }]);
    }
  };

  // Toggle all segments in a connection (when clicking main card)
  const toggleAllSegmentsInConnection = (connection: ConnectionFlight) => {
    const connectionId =
      connection.compositeId ||
      `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;

    const allSelected = areAllSegmentsSelected(connection);

    if (allSelected) {
      // Deselect all segments of this connection
      setSelectedSegments(
        selectedSegments.filter((sel) => sel.connectionId !== connectionId),
      );
    } else {
      // Select all segments of this connection
      const newSegments = connection.segments
        .filter((segment) => !isSegmentSelected(segment, connectionId))
        .map((segment) => ({ segment, connectionId }));
      setSelectedSegments([...selectedSegments, ...newSegments]);
    }
  };

  // Legacy function - kept for backward compatibility but now uses segment-based logic
  const toggleConnectionSelection = (connection: ConnectionFlight) => {
    toggleAllSegmentsInConnection(connection);
  };

  const handlePostRequest = async () => {
    if (selectedFlights.length === 0 && selectedSegments.length === 0) {
      showToast(
        "Please select at least one flight to post a request.",
        "error",
        3000,
      );
      return;
    }

    const parseDate = (dateStr: string) => {
      try {
        const parts = dateStr?.split(" ");
        if (parts.length !== 3) throw new Error("Unexpected date format");

        const monthNames: { [key: string]: number } = {
          January: 1,
          February: 2,
          March: 3,
          April: 4,
          May: 5,
          June: 6,
          July: 7,
          August: 8,
          September: 9,
          October: 10,
          November: 11,
          December: 12,
        };

        const month = monthNames[parts[0]];
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (!month || isNaN(day) || isNaN(year))
          throw new Error("Invalid date parts");

        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      } catch (error) {
        console.error("Error parsing date:", dateStr, error);
        return null;
      }
    };

    // Process direct flights
    const directFlightRequests = selectedFlights.map((flight) => {
      return {
        airlineId: flight.airlineId,
        flightNumber: flight.flightNumber,
        sourceCode: flight.departureCode,
        destinationCode: flight.arrivalCode,
        departureDate: new Date(`${flight.departureTime}Z`).toISOString(),
        arrvalDate: new Date(`${flight.arrivalTime}Z`).toISOString(),
        depterm: flight.depterminal || "N/A",
        arrterm: flight.arrterminal || "N/A",
        duration: flight.duration || "00:00",
        elptim: flight.elptim || "00:00",
        depcity: flight.depctry || "N/A",
        arrcity: flight.arrctry || "N/A",
      };
    });

    // Process individual selected segments - post each segment as a separate request
    const connectionFlightRequests = selectedSegments.map((selectedSeg) => {
      const segment = selectedSeg.segment;
      return {
        airlineId: segment.airlineId,
        flightNumber: segment.flightNumber,
        sourceCode: segment.departureCode,
        destinationCode: segment.arrivalCode,
        departureDate: new Date(`${segment.departureTime}Z`).toISOString(),
        arrvalDate: new Date(`${segment.arrivalTime}Z`).toISOString(),
        depterm: segment.depterminal || "N/A",
        arrterm: segment.arrterminal || "N/A",
        duration: segment.duration || "00:00",
        elptim: segment.duration || "00:00",
        depcity: segment.departureAirportName?.split(" ")[0] || "N/A",
        arrcity: segment.arrivalAirportName?.split(" ")[0] || "N/A",
      };
    });

    const requestData = {
      postRequest: [...directFlightRequests, ...connectionFlightRequests],
    };

    try {
      // console.log("Prepared request data for posting:", requestData);

      showLoader();
      const response = await postFlightRequest(requestData);
      console.log("Post request successful:", response);
      showToast("Flight request posted successfully!", "success", 3000);

      // Call the callback to refresh recent flights
      onPostRequestSuccess?.();
      setSelectedFlights([]);
      setSelectedConnections([]);
      setSelectedSegments([]); // Clear selected segments
      // fetchUserCreditBalance()
      setTimeout(() => {
        window.location.reload();
      }, 600);
    } catch (error: any) {
      console.error("Failed to post flight request:", error);
      const backendMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to post flight request. Please try again.";

      showToast(backendMessage, "error", 3000);
    } finally {
      hideLoader();
    }
  };

  // Render connection flight card
  const renderConnectionCard = (
    connection: ConnectionFlight,
    index: number,
  ) => {
    const connectionCompositeId =
      connection.compositeId ||
      `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;
    const allSegmentsSelected = areAllSegmentsSelected(connection);
    const [firstSegment, secondSegment] = connection.segments;

    // Render a single segment with checkbox
    const renderSegment = (segment: FlightSegment, segmentIndex: number) => {
      const isSegSelected = isSegmentSelected(segment, connectionCompositeId);

      return (
        <div
          key={segment.compositeId || `segment-${segment.id}-${segmentIndex}`}
          className={`rounded-lg p-3 mb-3 border transition-all ${
            readOnly ? "" : "cursor-pointer"
          } ${
            !readOnly && isSegSelected
              ? "border-[#FFD700] bg-yellow-50"
              : "border-gray-200 bg-white" + (readOnly ? "" : " hover:bg-gray-50")
          }`}
          onClick={(e) => {
            if (readOnly) return;
            toggleSegmentSelection(segment, connectionCompositeId, e);
          }}
        >
          {/* Segment Header with Checkbox */}
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              {/* Checkbox */}
              {!readOnly && (
                <input
                  type="checkbox"
                  checked={isSegSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleSegmentSelection(segment, connectionCompositeId);
                  }}
                  className="w-4 h-4 text-[#FFD700] border-gray-300 rounded focus:ring-[#FFD700] cursor-pointer"
                />
              )}
              <img
                className="w-10 h-10 object-contain rounded"
                src={segment.airlineLogo || DefaultAirLogo}
                alt={segment.airlineName}
                onError={(e) => {
                  e.currentTarget.src = DefaultAirLogo;
                }}
                loading="lazy"
              />
              <div>
                <p className="text-lightgray-200 text-xs">Airlines</p>
                <p className="text-grease-100 mt-1 text-xs font-medium">
                  {segment.airlineName}
                </p>
              </div>
            </div>
            <div>
              <p className="text-lightgray-200 text-xs">Flight No.</p>
              <p className="text-grease-100 mt-1 text-xs font-medium">
                {segment.flightNumber}
              </p>
            </div>
          </div>

          {/* Segment Flight Details */}
          <div className="bg-lightblue-100 p-3 rounded-lg flex items-center gap-4">
            <div className="flex flex-col items-start gap-1 min-w-[80px]">
              <p className="text-grease-100 text-xs font-medium">
                {segment.departureCode}
              </p>
              <p className="text-lightgray-200 text-[10px]">
                {segment.departureAirportName?.split(" ")[0]}
              </p>
              <p className="text-lightgray-200 text-[10px]">
                {formatDateTime(segment.departureTime, {
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </p>
            </div>

            <div className="flex flex-col items-center w-full">
              <div className="flex items-center w-full">
                <div className="w-2 h-2 bg-blue-100 rounded-full flex-shrink-0"></div>
                <div className="flex-1 border-b border-dashed border-indigo-200 mx-1"></div>
                <i className="st-flight-slim text-royalblue-100 text-xs mx-1" />
                <div className="flex-1 border-b border-dashed border-indigo-200 mx-1"></div>
                <div className="w-2 h-2 bg-blue-100 rounded-full flex-shrink-0"></div>
              </div>
              <p className="text-lightgray-150 text-[10px] text-center mt-1">
                {segment.duration
                  ? `${parseInt(segment.duration.slice(0, 2), 10)}h ${segment.duration.slice(3, 5)}m`
                  : ""}
              </p>
            </div>

            <div className="flex flex-col items-start gap-1 min-w-[80px]">
              <p className="text-grease-100 text-xs font-medium">
                {segment.arrivalCode}
              </p>
              <p className="text-lightgray-200 text-[10px]">
                {segment.arrivalAirportName?.split(" ")[0]}
              </p>
              <p className="text-lightgray-200 text-[10px]">
                {formatDateTime(segment.arrivalTime, {
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </p>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div
        key={
          connectionCompositeId ||
          `connection-${connection.segments[0].id}-${index}`
        }
        className={`bg-white rounded-lg p-5 border ${
          readOnly ? "" : "cursor-pointer"
        } ${
          !readOnly && allSegmentsSelected
            ? "border-[#FFD700] border-xl shadow-lg"
            : "border-gray-200"
        }`}
        onClick={() => {
          if (readOnly) return;
          toggleAllSegmentsInConnection(connection);
        }}
      >
        {/* Header with via airport and Select All checkbox */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            {/* Select All Checkbox */}
            {!readOnly && (
              <input
                type="checkbox"
                checked={allSegmentsSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleAllSegmentsInConnection(connection);
                }}
                className="w-4 h-4 text-[#FFD700] border-gray-300 rounded focus:ring-[#FFD700] cursor-pointer flex-shrink-0 mt-1"
                title="Select all segments"
              />
            )}
            <div className="flex-1">
              <p className="text-sm text-grease-100 font-medium">
                via{" "}
                {connection.connectionAirportName ||
                  connection.connectionAirport}
              </p>
              <p className="text-xs text-lightgray-200 mt-1">
                {(() => {
                  const hours = Math.floor(connection.layoverMinutes / 60);
                  const minutes = connection.layoverMinutes % 60;
                  return hours > 0
                    ? `${hours}h ${minutes}m layover`
                    : `${minutes}m layover`;
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* First Segment */}
        {renderSegment(firstSegment, 0)}

        {/* Layover indicator */}
        <div className="flex items-center justify-center my-2">
          <div className="flex items-center gap-2 px-3 py-1 bg-yellow-50 rounded-full">
            <i className="st-clock text-yellow-600 text-xs"></i>
            <p className="text-xs text-yellow-700 font-medium">
              {(() => {
                const hours = Math.floor(connection.layoverMinutes / 60);
                const minutes = connection.layoverMinutes % 60;
                return hours > 0
                  ? `${hours}h ${minutes}m layover in ${connection.connectionAirport}`
                  : `${minutes}m layover in ${connection.connectionAirport}`;
              })()}
            </p>
          </div>
        </div>

        {/* Second Segment */}
        {renderSegment(secondSegment, 1)}

        {/* Total Duration */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-grease-100 text-center">
            Total duration: {connection.totalDuration}
          </p>
        </div>
      </div>
    );
  };

  const d = new Date(searchDate);
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const month = d.toLocaleDateString(undefined, { month: "long" });
  const day = d.getDate();
  const year = d.getFullYear();
  const formattedDate = `${month} ${day} ${year} - ${weekday}`;

  // Format selected dates for display
  const selectedDatesDisplay =
    selectedDates.length > 0
      ? selectedDates
          .map((date) => {
            const d = new Date(date);
            const weekday = d.toLocaleDateString(undefined, {
              weekday: "long",
            });
            const month = d.toLocaleDateString(undefined, { month: "long" });
            const day = d.getDate();
            const year = d.getFullYear();
            return `${month} ${day} ${year} - ${weekday}`;
          })
          .join(", ")
      : formattedDate;

  // Build dummy airline filter list from current flights data
  const airlineFilterList = useMemo(() => {
    const airlineMap = new Map<string, { name: string; count: number }>();
    flights.forEach((f) => {
      const name = f.airlineName || "Unknown";
      const existing = airlineMap.get(name);
      airlineMap.set(name, { name, count: (existing?.count || 0) + 1 });
    });
    connectionFlights.forEach((c) => {
      c.segments.forEach((s) => {
        const name = s.airlineName || "Unknown";
        const existing = airlineMap.get(name);
        airlineMap.set(name, { name, count: (existing?.count || 0) + 1 });
      });
    });
    return Array.from(airlineMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [flights, connectionFlights]);

  // Dummy flight time ranges
  const flightTimeRanges = [
    { label: "12:00 AM-5:59 AM", count: 0 },
    { label: "6:00 AM-11:59 AM", count: 0 },
    { label: "12:00 PM-5:59 PM", count: 0 },
    { label: "6:00 PM-11:59 PM", count: 0 },
  ];

  // Compute flight time range counts from actual data
  const flightTimeRangeCounts = useMemo(() => {
    const counts = [0, 0, 0, 0]; // 12-5:59AM, 6-11:59AM, 12-5:59PM, 6-11:59PM
    const getHour = (timeStr: string) => {
      try {
        return new Date(timeStr).getUTCHours();
      } catch {
        return -1;
      }
    };
    flights.forEach((f) => {
      const h = getHour(f.departureTime);
      if (h >= 0 && h < 6) counts[0]++;
      else if (h >= 6 && h < 12) counts[1]++;
      else if (h >= 12 && h < 18) counts[2]++;
      else if (h >= 18) counts[3]++;
    });
    connectionFlights.forEach((c) => {
      const h = getHour(c.overallDepartureTime);
      if (h >= 0 && h < 6) counts[0]++;
      else if (h >= 6 && h < 12) counts[1]++;
      else if (h >= 12 && h < 18) counts[2]++;
      else if (h >= 18) counts[3]++;
    });
    return counts;
  }, [flights, connectionFlights]);

  // Group flights by date for rendering date headers
  const flightsByDate = useMemo(() => {
    const groups: { date: string; label: string; flights: FlightDetails[] }[] =
      [];
    const dateMap = new Map<string, FlightDetails[]>();

    flights.forEach((flight) => {
      const dateKey =
        flight.scheduleDate ||
        flight.date ||
        flight.departureTime?.split("T")[0] ||
        "unknown";
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
      dateMap.get(dateKey)!.push(flight);
    });

    // Sort date keys
    const sortedKeys = Array.from(dateMap.keys()).sort();
    sortedKeys.forEach((dateKey) => {
      let label = dateKey;
      try {
        const d = new Date(dateKey + "T00:00:00");
        if (!isNaN(d.getTime())) {
          const month = d.toLocaleDateString("en-US", { month: "long" });
          const day = d.getDate();
          const year = d.getFullYear();
          const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
          label = `${month} ${day} ${year} - ${weekday}`;
        }
      } catch {
        /* keep raw key */
      }
      groups.push({ date: dateKey, label, flights: dateMap.get(dateKey)! });
    });

    return groups;
  }, [flights]);

  const displayedResultCount =
    totalResults || flights.length + connectionFlights.length;

  const routeTitle =
    sourceLabel && destinationLabel
      ? `Search Results:  ${sourceLabel} to ${destinationLabel}`
      : sourceLabel
        ? `Search Results:  ${sourceLabel}`
        : "Search Results";

  if (!show) return null;

  // Show "No Data Found" only when not loading and no results
  const showNoData =
    !isLoadingFlights && flights.length === 0 && connectionFlights.length === 0;

  return (
    <section className="mt-6 searchResult px-1">
      <div className="search-results mt-4">
        {/* Two-column layout: Filters sidebar + Results */}
        <div className={`${hideInternalFilters ? "" : "flex gap-6"}`}>
          {/* Left Sidebar - Filters (aligned to top of results area) */}
          <div
            className={`w-[200px] flex-shrink-0 ${hideInternalFilters ? "hidden" : "hidden md:block"}`}
          >
            <div className="sticky top-4">
              {/* Filters Header */}
              <div className="flex items-center justify-between mb-1">
                <p className="text-[#1F2937] text-base font-bold">Filters</p>
                <button
                  type="button"
                  className="text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
                  title="Reset filters"
                  onClick={() => onStopsFilterChange("none")}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-[#9CA3AF] text-xs mb-5">
                Showing {displayedResultCount} results
              </p>

              {/* Airlines Filter */}
              {/* <div className="mb-5">
                <p className="text-[#1F2937] text-xs font-semibold mb-3">Airlines</p>
                <div className="flex flex-col gap-2">
                  {airlineFilterList.length > 0 ? (
                    airlineFilterList.map((airline) => (
                      <label key={airline.name} className="flex items-center justify-between cursor-pointer group">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            defaultChecked
                            className="w-3.5 h-3.5 text-[#FBBF24] border-gray-300 rounded focus:ring-[#FBBF24]"
                          />
                          <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">{airline.name}</span>
                        </div>
                        <span className="text-[10px] text-[#9CA3AF]">{airline.count}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-xs text-[#9CA3AF]">No airlines</p>
                  )}
                </div>
              </div> */}

              {/* Flight Times Filter */}
              {/* <div className="mb-5">
                <p className="text-[#1F2937] text-xs font-semibold mb-3">Flight times</p>
                <div className="flex flex-col gap-2">
                  {flightTimeRanges.map((range, idx) => (
                    <label key={range.label} className="flex items-center justify-between cursor-pointer group">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="w-3.5 h-3.5 text-[#FBBF24] border-gray-300 rounded focus:ring-[#FBBF24]"
                        />
                        <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">{range.label}</span>
                      </div>
                      <span className="text-[10px] text-[#9CA3AF]">{flightTimeRangeCounts[idx] || "--"}</span>
                    </label>
                  ))}
                </div>
              </div> */}

              {/* Stops Filter */}
              <div className="mb-2">
                <p className="text-[#1F2937] text-sm font-semibold mb-3">
                  Stops
                </p>
                <div className="flex flex-col gap-3" ref={stopsDropdownRef}>
                  <label className="flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="stops"
                        checked={stopsFilter === "none"}
                        onChange={() => onStopsFilterChange("none")}
                        className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                      />
                      <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                        Non-stop
                      </span>
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="stops"
                        checked={stopsFilter === "one"}
                        onChange={() => onStopsFilterChange("one")}
                        className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                      />
                      <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                        1 Stop
                      </span>
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="stops"
                        checked={stopsFilter === "two"}
                        onChange={() => onStopsFilterChange("two")}
                        className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                      />
                      <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                        2 Stops
                      </span>
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="stops"
                        checked={stopsFilter === "three"}
                        onChange={() => onStopsFilterChange("three")}
                        className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                      />
                      <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                        3 Stops
                      </span>
                    </div>
                  </label>
                  {!hideConnectedFilter && (
                    <label className="flex items-center justify-between cursor-pointer group">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="stops"
                          checked={stopsFilter === "one-or-fewer"}
                          onChange={() => onStopsFilterChange("one-or-fewer")}
                          className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                        />
                        <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                          Connected
                        </span>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Results */}
          <div className="flex-1 min-w-0">
            <div className="border-b pb-3 sm:pb-4 mb-3 sm:mb-4">
              <p className="text-grease-100 text-base sm:text-lg font-semibold">
                {routeTitle}
              </p>
              <div className="flex items-center gap-2 mt-1.5 sm:mt-2 text-lightgray-200 text-xs sm:text-sm">
                <i className="st-calendar text-sm sm:text-base"></i>
                <span className="truncate">{selectedDatesDisplay}</span>
              </div>
            </div>
            {isLoadingFlights ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, index) => (
                  <FlightCardSkeleton key={index} />
                ))}
              </div>
            ) : showNoData ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                  <svg
                    className="w-7 h-7 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {stopsFilter === "none" ? (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No direct flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      Try searching with a stopover to see more options.
                    </p>
                    <button
                      onClick={() => onStopsFilterChange("one-or-fewer")}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Search flights with one stop
                    </button>
                  </>
                ) : stopsFilter === "one-or-fewer" ? (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      We couldn't find any flights matching your filters.
                    </p>
                    <button
                      onClick={() => onStopsFilterChange("none")}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Reset to non-stop
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      We couldn't find any flights matching your filters.
                    </p>
                    <button
                      onClick={() => onStopsFilterChange("none")}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Reset to non-stop
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Date-grouped flight cards */}
                {flightsByDate.map((group) => (
                  <div key={group.date} className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="st-calendar text-gray-400 text-sm"></i>
                      <p className="text-grease-100 text-sm font-medium">
                        {group.label}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {group.flights.map((flight) => {
                        const flightCompositeId =
                          flight.compositeId || String(flight.id);
                        const isSelected = selectedFlights.some(
                          (f) =>
                            (f.compositeId || String(f.id)) ===
                            flightCompositeId,
                        );
                        return (
                          <div
                            key={flightCompositeId}
                            className={`relative bg-white rounded-xl p-4 border transition-all ${
                              readOnly ? "" : "cursor-pointer"
                            } ${
                              !readOnly && isSelected
                                ? "border-[#FFD700] shadow-lg"
                                : "border-gray-100" +
                                  (readOnly ? "" : " hover:border-gray-200")
                            } ${hasLiveData(flight) ? "mt-3" : ""}`}
                            onClick={() => {
                              if (readOnly) return;
                              toggleFlightSelection(flight);
                            }}
                          >
                            {(() => {
                              const liveDep =
                                flight.actualDeparture ||
                                flight.estimatedDeparture;
                              const liveArr = flight.estimatedArrival;
                              const depTime = liveDep || flight.departureTime;
                              const arrTime = liveArr || flight.arrivalTime;
                              const liveDurMin = flight.liveDurationMinutes;
                              const durationText =
                                liveDurMin != null && liveDurMin > 0
                                  ? `${Math.floor(liveDurMin / 60)}h ${liveDurMin % 60}m`
                                  : flight?.elptim
                                    ? `${parseInt(flight.elptim.slice(0, 3), 10)}h ${parseInt(flight.elptim.slice(3, 5), 10)}m`
                                    : "";
                              const statusColors = (() => {
                                const s = flight.liveStatus;
                                if (s === "Landed" || s === "Arrived")
                                  return {
                                    bg: "bg-green-500",
                                    text: "text-white",
                                  };
                                if (s === "Cancelled")
                                  return {
                                    bg: "bg-red-500",
                                    text: "text-white",
                                  };
                                if (s === "Early")
                                  return {
                                    bg: "bg-blue-500",
                                    text: "text-white",
                                  };
                                if (
                                  flight.delayMinutes &&
                                  flight.delayMinutes > 0
                                )
                                  return {
                                    bg: "bg-orange-500",
                                    text: "text-white",
                                  };
                                return {
                                  bg: "bg-blue-500",
                                  text: "text-white",
                                };
                              })();
                              return (
                                <>
                                  {/* Floating status pill at top-left */}
                                  {flight.liveStatus && (
                                    <span
                                      className={`absolute -top-3 left-4 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-medium shadow-sm ${statusColors.bg} ${statusColors.text}`}
                                    >
                                      <svg
                                        className="w-2.5 h-2.5"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      {flight.liveStatus}
                                      {flight.delayMinutes != null &&
                                        flight.delayMinutes !== 0 && (
                                          <span className="ml-0.5">
                                            {flight.delayMinutes > 0
                                              ? `+${flight.delayMinutes}m`
                                              : `${flight.delayMinutes}m`}
                                          </span>
                                        )}
                                    </span>
                                  )}
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-3 min-w-[120px]">
                                      <img
                                        className="w-10 h-10 object-contain rounded-full"
                                        src={
                                          flight.airlineLogo || DefaultAirLogo
                                        }
                                        alt={flight.airlineName}
                                        onError={(e) => {
                                          e.currentTarget.src = DefaultAirLogo;
                                        }}
                                        loading="lazy"
                                      />
                                      <div>
                                        <p className="text-grease-100 text-sm font-semibold leading-tight">
                                          {flight.airlineName}
                                        </p>
                                        <p className="text-gray-400 text-xs mt-0.5">
                                          {flight.flightNumber}
                                        </p>
                                      </div>
                                    </div>
                                    {/* Terminal + Gate inline */}
                                    {(flight.terminal || flight.gate) && (
                                      <div className="flex items-stretch gap-3">
                                        {flight.terminal && (
                                          <div className="flex flex-col">
                                            <span className="text-gray-400 text-[10px] leading-tight">
                                              Terminal
                                            </span>
                                            <span className="text-grease-100 text-sm font-semibold leading-tight">
                                              {flight.terminal}
                                            </span>
                                          </div>
                                        )}
                                        {flight.terminal && flight.gate && (
                                          <div className="w-px bg-gray-200 self-stretch" />
                                        )}
                                        {flight.gate && (
                                          <div className="flex flex-col">
                                            <span className="text-gray-400 text-[10px] leading-tight">
                                              Gate
                                            </span>
                                            <span className="text-grease-100 text-sm font-semibold leading-tight">
                                              {flight.gate}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <div className="flex flex-col items-center min-w-[60px]">
                                      <div className="flex items-center gap-1">
                                        <i className="st-top-right text-gray-400 text-[8px]" />
                                        <p className="text-gray-400 text-xs">
                                          {formatDateTime(depTime, {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                          })}
                                        </p>
                                      </div>
                                      <p className="text-grease-100 text-base font-bold mt-0.5">
                                        {flight.departureCode}
                                      </p>
                                      <p className="text-gray-400 text-[10px] mt-0.5">
                                        {
                                          flight.departureAirportName?.split(
                                            " ",
                                          )[0]
                                        }
                                      </p>
                                    </div>
                                    <div className="flex flex-col items-center flex-1 px-2">
                                      <div className="flex items-center w-full">
                                        <div className="w-2 h-2 rounded-full border-2 border-light-yellow flex-shrink-0" />
                                        <div className="flex-1 border-b-2 border-dashed border-gray-200 mx-1" />
                                        <i className="st-flight-slim text-grease-100 text-sm mx-1" />
                                        <div className="flex-1 border-b-2 border-dashed border-gray-200 mx-1" />
                                        <div className="w-2 h-2 rounded-full border-2 border-light-yellow flex-shrink-0" />
                                      </div>
                                      <p className="text-gray-400 text-[10px] text-center mt-1">
                                        {durationText}
                                      </p>
                                      {flight?.stops > 0 && (
                                        <p className="text-orange-500 text-[10px]">
                                          {flight.stops}{" "}
                                          {flight.stops === 1
                                            ? "Stop"
                                            : "Stops"}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-center min-w-[60px]">
                                      <div className="flex items-center gap-1">
                                        <i className="st-bottom-right text-gray-400 text-[8px]" />
                                        <p className="text-gray-400 text-xs">
                                          {formatDateTime(arrTime, {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                          })}
                                          {flight.arrday &&
                                            Number(flight.arrday) > 0 && (
                                              <span className="ml-0.5 text-[10px] font-medium text-red-500">
                                                +{flight.arrday}
                                              </span>
                                            )}
                                        </p>
                                      </div>
                                      <p className="text-grease-100 text-base font-bold mt-0.5">
                                        {flight.arrivalCode}
                                      </p>
                                      <p className="text-gray-400 text-[10px] mt-0.5">
                                        {
                                          flight.arrivalAirportName?.split(
                                            " ",
                                          )[0]
                                        }
                                      </p>
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Connection flights (not date-grouped) */}
                {connectionFlights.length > 0 && (
                  <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {connectionFlights.map((connection, index) =>
                        renderConnectionCard(connection, index),
                      )}
                    </div>
                  </div>
                )}

                {!readOnly &&
                  (selectedFlights.length > 0 ||
                    selectedSegments.length > 0) && (
                    <div className="flex justify-center">
                      <button
                        className="p-4 w-60 rounded-lg text-white text-center text-sm mt-6 bg-[#FFBC00] shadow-button"
                        onClick={handlePostRequest}
                      >
                        POST {selectedFlights.length + selectedSegments.length}{" "}
                        REQUEST
                        {selectedFlights.length + selectedSegments.length !== 1
                          ? "S"
                          : ""}
                      </button>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchResults;
